import { useState, type CSSProperties } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faGripVertical, faUpRightFromSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { WorkflowStatusBadge, deriveBadgeState } from '../../components';
import { useTwoClickConfirm } from '../../hooks';
import { formatLessonDuration } from '../../utils/formatLessonDuration';
import { formatLessonHumanId } from '../../utils/humanIds';
import type { CourseLessonRowItem } from './types';

interface SortableCourseLessonRowProps {
  lesson: CourseLessonRowItem;
  index: number;
  styles: Record<string, CSSProperties>;
  isClientContext: boolean;
  onOpen: (lessonId: string) => void;
  onRemove: (entryId: string) => void;
  onReEnable: (entryId: string) => void;
}

function SortableCourseLessonRow({
  lesson,
  index,
  styles,
  isClientContext,
  onOpen,
  onRemove,
  onReEnable,
}: SortableCourseLessonRowProps) {
  // In client context only client-added lessons are reorderable; base lessons
  // keep their base-relative order (mirrors the lesson editor's block gating).
  const dragDisabled = isClientContext && !lesson.clientAdded;
  // Hiding a base lesson for the account is reversible (Re-enable), so it acts
  // on a single click; true removals use the lesson editor's two-click confirm
  // (X arms into a checkmark, auto-resets after 3s).
  const removeMode: 'hide' | 'delete' = isClientContext && !lesson.clientAdded ? 'hide' : 'delete';
  const { armed: showDeleteConfirm, trigger: triggerRemoveConfirm, reset: resetRemoveConfirm } =
    useTwoClickConfirm(() => onRemove(lesson.entryId));

  const handleRemoveClick = () => {
    if (removeMode === 'hide') {
      onRemove(lesson.entryId);
      return;
    }
    triggerRemoveConfirm();
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lesson.entryId, disabled: dragDisabled });

  const rowStyle: CSSProperties = {
    ...styles.lessonRow,
    ...(isDragging ? styles.lessonRowDragging : {}),
    ...(lesson.suppressed ? { opacity: 0.55 } : {}),
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={rowStyle} role="listitem" onMouseLeave={resetRemoveConfirm}>
      <button
        type="button"
        style={{
          ...styles.dragHandle,
          ...(dragDisabled ? { opacity: 0, pointerEvents: 'none' as const } : {}),
        }}
        {...listeners}
        {...attributes}
        aria-label={`Drag to reorder ${lesson.title}`}
        aria-hidden={dragDisabled}
        {...(dragDisabled ? { tabIndex: -1 } : {})}
      >
        {/* faGripVertical reads heavy at 16px; 12px keeps the handle subtle. */}
        <FontAwesomeIcon icon={faGripVertical} style={{ fontSize: 12 }} />
      </button>
      <span style={styles.positionBadge}>{index + 1}</span>
      <div style={{ minWidth: 0 }}>
        <div style={styles.lessonTitle}>{lesson.title}</div>
        <div style={styles.lessonMeta}>
          {formatLessonHumanId(lesson.numericId, lesson.id)}
          {lesson.suppressed ? ' — disabled for this account' : ''}
        </div>
      </div>
      <div style={styles.mutedText}>{formatLessonDuration(lesson.estimatedTimeMinutes)}</div>
      <WorkflowStatusBadge state={deriveBadgeState(lesson.workflowState, lesson.publishLifecycle)} />
      <div style={styles.lessonRowActions}>
        <button
          type="button"
          style={styles.iconButton}
          onClick={() => onOpen(lesson.id)}
          title="Open lesson in new tab"
          aria-label={`Open ${lesson.title} in new tab`}
        >
          <FontAwesomeIcon icon={faUpRightFromSquare} />
        </button>
        {lesson.suppressed ? (
          <button
            type="button"
            style={styles.reEnableButton}
            onClick={() => onReEnable(lesson.entryId)}
            aria-label={`Re-enable ${lesson.title} for this account`}
          >
            Re-enable
          </button>
        ) : (
          <button
            type="button"
            style={{
              ...styles.iconButton,
              ...styles.dangerIconButton,
              ...(removeMode !== 'hide' && showDeleteConfirm ? styles.dangerIconButtonConfirm : {}),
            }}
            onClick={handleRemoveClick}
            title={removeMode === 'hide'
              ? 'Hide for this account (re-enable from the list)'
              : showDeleteConfirm ? 'Click again to confirm' : 'Remove lesson'}
            aria-label={removeMode === 'hide'
              ? `Hide ${lesson.title} for this account`
              : `Remove ${lesson.title} from course`}
          >
            <FontAwesomeIcon icon={removeMode !== 'hide' && showDeleteConfirm ? faCheck : faXmark} />
          </button>
        )}
      </div>
    </div>
  );
}

function InsertLessonControl({
  insertIndex,
  styles,
  onOpen,
}: {
  insertIndex: number;
  styles: Record<string, CSSProperties>;
  onOpen: (insertIndex: number) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isVisible = isHovered || isFocused;

  return (
    <div
      style={styles.insertControl}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        style={{
          ...styles.insertButton,
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
        onClick={() => onOpen(insertIndex)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        + Insert lesson
      </button>
    </div>
  );
}

interface LessonsInCourseSectionProps {
  styles: Record<string, CSSProperties>;
  selectedLessons: CourseLessonRowItem[];
  totalEstimatedMinutes: number;
  isClientContext: boolean;
  /** Insert index used by the header/footer "+ Add lesson" buttons. */
  addLessonEndIndex: number;
  onOpenAddLessonModal: (insertIndex: number) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onOpenLesson: (lessonId: string) => void;
  onRemoveLesson: (entryId: string) => void;
  onReEnableLesson: (entryId: string) => void;
}

export function LessonsInCourseSection({
  styles,
  selectedLessons,
  totalEstimatedMinutes,
  isClientContext,
  addLessonEndIndex,
  onOpenAddLessonModal,
  onDragEnd,
  onOpenLesson,
  onRemoveLesson,
  onReEnableLesson,
}: LessonsInCourseSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <section style={{ ...styles.card, ...styles.rightColumn }}>
      <div style={styles.lessonHeader}>
        <div>
          <h2 style={styles.cardTitle}>Lessons in course</h2>
          <p style={styles.cardSubtitle}>
            {selectedLessons.length} {selectedLessons.length === 1 ? 'lesson' : 'lessons'} - {totalEstimatedMinutes} min
          </p>
        </div>
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => onOpenAddLessonModal(addLessonEndIndex)}
        >
          + Add lesson
        </button>
      </div>

      {selectedLessons.length === 0 ? (
        <div style={styles.emptyState}>
          No lessons in this course yet.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={selectedLessons.map((lesson) => lesson.entryId)} strategy={verticalListSortingStrategy}>
            <div style={styles.lessonList} role="list">
              <InsertLessonControl insertIndex={0} styles={styles} onOpen={onOpenAddLessonModal} />
              {selectedLessons.map((lesson, index) => (
                <div key={lesson.entryId}>
                  <SortableCourseLessonRow
                    lesson={lesson}
                    index={index}
                    styles={styles}
                    isClientContext={isClientContext}
                    onOpen={onOpenLesson}
                    onRemove={onRemoveLesson}
                    onReEnable={onReEnableLesson}
                  />
                  <InsertLessonControl
                    insertIndex={index + 1}
                    styles={styles}
                    onOpen={onOpenAddLessonModal}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div style={styles.addLessonFooter}>
        <button
          type="button"
          style={styles.primaryButton}
          onClick={() => onOpenAddLessonModal(addLessonEndIndex)}
        >
          + Add lesson
        </button>
      </div>
    </section>
  );
}
